import type { Component, VoidComponent } from "solid-js";
import { createMemo, For, Show } from "solid-js";
import Text from "../../../../components/text/Text";
import type { IconName } from "../../../../components/icon/Icon";
import { Icon } from "../../../../components/icon/Icon";
import { A, useCurrentMatches } from "@solidjs/router";
import { Collapsible } from "@ark-ui/solid";
import { isNil } from "lodash-es";

type BaseSidebarItemProps = {
  label: string;
  icon?: IconName;
};

export type SidebarItemProps = {
  href: string;
} & BaseSidebarItemProps;

const SidebarItem: Component<SidebarItemProps> = (props) => (
  <A
    href={props.href}
    class="group flex gap-2.5 rounded-sm px-2.5 py-2 outline-offset-0 hover:bg-surface-extra-subtle-hover focus-visible:outline-default url-active:bg-surface-active"
  >
    <Item {...props} />
  </A>
);

type SidebarMenuProps = {
  items: SidebarItemProps[];
} & BaseSidebarItemProps;

const SidebarMenu: VoidComponent<SidebarMenuProps> = (props) => {
  const matches = useCurrentMatches();
  const hasActivePath = createMemo(() =>
    matches().some(({ path }) => props.items.map(({ href }) => href).includes(path)),
  );

  return (
    <Collapsible.Root defaultOpen={hasActivePath()}>
      <Collapsible.Trigger class="w-full rounded-sm px-2.5 py-2 hover:bg-surface-extra-subtle-hover focus-visible:outline-default">
        <div class="flex items-center justify-between gap-3">
          <Item {...props} />

          <Collapsible.Indicator
            asChild={(indicatorProps) => (
              <Icon
                {...indicatorProps}
                name="chevronDown"
                type="solid"
                size="md"
                class="icon-secondary transition-transform open:rotate-180"
              />
            )}
          />
        </div>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <div class="flex flex-col gap-0.5">
          <For each={props.items}>{(subItem) => <SidebarItem {...subItem} />}</For>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const Item: VoidComponent<BaseSidebarItemProps> = (props) => (
  <div class="flex min-w-0 gap-2.5">
    <Show when={props.icon}>
      {(icon) => (
        <Icon name={icon()} size="lg" type="solid" class="shrink-0 icon-tertiary group-url-active:icon-secondary" />
      )}
    </Show>
    <Text
      text={props.label}
      truncate
      class="text-base-medium text-primary"
      classList={{ "pl-7.5": isNil(props.icon) }}
    />
  </div>
);

export { SidebarItem, SidebarMenu };
